import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categoryMeta, videos, type VideoType } from "@/mocks/videos";
import { typeOrder } from "@/utils/format";
import FeedbackForm from "./components/FeedbackForm";

const stats = [
  { label: "视频内容", value: "10,000+", icon: "ri-video-line" },
  { label: "覆盖分类", value: "4 大", icon: "ri-layout-grid-line" },
  { label: "每日上新", value: "200+", icon: "ri-calendar-check-line" },
  { label: "活跃用户", value: "500万", icon: "ri-user-heart-line" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 在线视频",
    description:
      "了解在线视频平台的网站定位、服务范围与内容介绍，并通过反馈表单与我们取得联系。",
    keywords: "在线视频,关于在线视频,视频平台介绍,意见反馈",
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        {/* Banner */}
        <section className="w-full px-4 md:px-8 pt-10 md:pt-14">
          <div className="max-w-[1200px] mx-auto">
            <nav
              aria-label="面包屑"
              className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5"
            >
              <Link to="/" className="hover:text-primary-600 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950">
              关于 <span className="text-primary-600">在线视频</span>
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed">
              在线视频是一家海量视频资源在线观看平台，面向喜爱影视剧、综艺节目及短视频的用户群体，
              致力于提供丰富视频内容的浏览与在线播放服务，帮助每一位用户快速发现并观看感兴趣的好内容。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-8 mt-10">
          <div className="max-w-[1200px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div
                key={s.label}
                className="p-5 rounded-lg bg-background-50 border border-background-200"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <p className="mt-4 text-2xl font-bold text-foreground-950">
                  {s.value}
                </p>
                <p className="mt-1 text-sm text-foreground-500">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Positioning & services */}
        <section className="w-full px-4 md:px-8 mt-14 md:mt-16">
          <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                  网站定位
                </h2>
              </div>
              <p className="text-sm md:text-base leading-relaxed text-foreground-700">
                我们相信，好的内容值得被更多人看见。在线视频以清晰的内容分类、便捷的筛选搜索和舒适的播放浏览体验为核心，
                让用户在电影、电视剧、综艺、短视频之间自由穿梭，用最少的步骤找到想看的内容。
              </p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
                平台始终坚持内容清晰、界面简洁、体验流畅的原则，不论是在电脑还是手机上，
                都能获得一致而愉悦的浏览体验。
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                  服务范围与内容
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {typeOrder.map((type: VideoType) => {
                  const meta = categoryMeta[type];
                  const count = videos.filter((v) => v.type === type).length;
                  return (
                    <Link
                      key={type}
                      to={`/category/${meta.slug}`}
                      className="p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-foreground-950">
                          {meta.label}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">
                          {count} 部
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-foreground-500 line-clamp-2">
                        {meta.intro}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Feedback */}
        <section className="w-full px-4 md:px-8 mt-14 md:mt-16 pb-4">
          <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-1.5 h-6 rounded-full bg-secondary-500"></span>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                  联系我们
                </h2>
              </div>
              <p className="text-sm leading-relaxed text-foreground-700">
                有任何建议、内容推荐或合作意向，都欢迎通过右侧表单留言，
                我们会认真阅读每一条反馈。
              </p>
              <ul className="mt-5 space-y-3 text-sm text-foreground-700">
                <li className="flex items-center gap-3 p-3 rounded-md bg-background-100">
                  <i className="ri-mail-line text-primary-600 text-lg"></i>
                  service@zaixianshipin.example
                </li>
                <li className="flex items-center gap-3 p-3 rounded-md bg-background-100">
                  <i className="ri-time-line text-primary-600 text-lg"></i>
                  服务时间：每日 9:00 - 22:00
                </li>
                <li className="flex items-center gap-3 p-3 rounded-md bg-background-100">
                  <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                  内容合作：content@zaixianshipin.example
                </li>
              </ul>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                  意见反馈
                </h2>
              </div>
              <FeedbackForm />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}